import { forwardRef, useImperativeHandle, useRef } from 'react';

import { iterateSlots } from './iterate-slots.js';
import {
  mapSelectedSeriesToTimeseries,
  mapSelectionDomainToTimeseries,
} from './map-select-actions.js';
import {
  mapBandSeriesActionsToXY,
  mapSeriesActionsToXY,
} from './map-series-actions.js';
import { resolveValueRepresentation } from './resolve-value-representation.js';
import { transformTimeseriesAnnotationsToXY } from './transformer-annotations-to-xy.js';
import { transformTimeseriesDataToXYChart } from './transformer-data-to-xy.js';
import {
  type ChartVariantInternal,
  DEFAULT_VARIANT,
  type TimeseriesChartProps,
  type TimeseriesChartRef,
} from './types/timeseries.js';
import { buildBuckets } from './utils/build-buckets.js';
import { buildTimeseriesImperativeHandle } from './utils/build-timeseries-imperative-handle.js';
import { formatCSV } from './utils/download/format-csv.js';
import {
  mapXAxisSlot,
  mapYAxisSlots,
  mapYAxisSlotToProps,
} from './utils/map-axis-slots.js';
import { resolveTimeseriesColorRules } from './utils/map-timeseries-to-xy-color-rules.js';
import {
  defineAxisId,
  renderAreaSeries,
  renderBandSeries,
  renderBarSeries,
  renderLineSeries,
  xAxisId,
} from './utils/render-series.js';
import { resolveGapPolicy } from './utils/resolve-gap-policy.js';
import { downloadCSV } from '../../../core/utils/download.js';
import { getChartStatesTemplates } from '../../core/components/chart-states/utils/get-chart-states-template.js';
import { CSS_DOWNLOAD_FILENAME } from '../../core/constants.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import type { XYChartRef } from '../../xy-chart/types/xy-chart.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { renderXYAnnotations } from '../shared/utils/render-xy-annotations.js';

export const TimeseriesToXY = forwardRef<
  TimeseriesChartRef,
  TimeseriesChartProps
>((props, forwardedRef) => {
  const {
    data = [],
    variant = DEFAULT_VARIANT,
    valueRepresentation: rootValueRepresentation,
    children,
    loading,
    className,
    style,
    width,
    height,
    colorPalette,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    truncationMode,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    seriesActions,
    gapPolicy,
    pointsDisplay,
  } = props;

  const {
    line,
    bar,
    area,
    band,
    shapePriority,
    disableZoom,
    disablePan,
    selectActions,
    legend,
    intents,
    colorRules,
    xAxis,
    leftYAxis,
    rightYAxis,
    annotations,
    toolbar,
    deprecatedToolbar,
  } = iterateSlots(children);

  const resolvedValueRepresentation = resolveValueRepresentation(
    rootValueRepresentation,
    ...line.map((s) => s.valueRepresentation),
    ...area.map((s) => s.valueRepresentation),
    ...bar.map((s) => s.valueRepresentation),
  );

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const {
    buckets,
    lineAreaBarData,
    bandData,
    allSeriesData,
    gapPolicies,
    order,
  } = buildBuckets(
    data,
    variant,
    { line, area, bar, band, shapePriority },
    pointsDisplay,
  );

  const result = transformTimeseriesDataToXYChart(buckets, order);
  const effectiveGapPolicy = resolveGapPolicy(gapPolicies, gapPolicy);

  const allColorRules = resolveTimeseriesColorRules(allSeriesData, colorRules);

  const xAxisConfig = mapXAxisSlot(xAxis);

  const { configuredAxes, renderedUnits } = mapYAxisSlots(
    leftYAxis,
    rightYAxis,
    [...result.units],
  );

  const mappedSeriesActions = seriesActions
    ? mapSeriesActionsToXY(seriesActions, lineAreaBarData)
    : undefined;

  const mappedBandActions = seriesActions
    ? mapBandSeriesActionsToXY(seriesActions, bandData)
    : undefined;

  const seriesForShape = (shape: ChartVariantInternal) => {
    switch (shape) {
      case 'line':
        return result.line.map((s) =>
          renderLineSeries(s, { actions: mappedSeriesActions }),
        );
      case 'area':
        return result.area.map((s) =>
          renderAreaSeries(s, { actions: mappedSeriesActions }),
        );
      case 'bar':
        return result.bar.map((s) =>
          renderBarSeries(s, { actions: mappedSeriesActions }),
        );
      case 'band':
        return result.band.map((s) =>
          renderBandSeries(s, { actions: mappedBandActions }),
        );
    }
  };

  const xyRef = useRef<XYChartRef>(null);

  useImperativeHandle(
    forwardedRef,
    () => buildTimeseriesImperativeHandle(xyRef),
    [],
  );

  const downloadDataCallback = () => {
    const csvString = formatCSV({
      line: buckets.line.map((s) => s.data),
      area: buckets.area.map((s) => s.data),
      bar: buckets.bar.map((s) => s.data),
      band: bandData,
    });
    downloadCSV(csvString, CSS_DOWNLOAD_FILENAME);
  };

  return (
    <XYChart
      data={[]}
      ref={xyRef}
      loading={loading}
      className={className}
      style={style}
      width={width}
      height={height}
      colorPalette={colorPalette}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      truncationMode={truncationMode}
      __downloadDataCallback={downloadDataCallback}
    >
      {errorState && <XYChart.ErrorState>{errorState}</XYChart.ErrorState>}
      {emptyState && <XYChart.EmptyState>{emptyState}</XYChart.EmptyState>}
      {order.flatMap(seriesForShape)}
      {selectActions ? (
        <XYChart.Select
          actions={(selectedSeries, selectionDomain) => {
            const hasNoDataInSelection = selectionDomain.length === 0;
            if (hasNoDataInSelection) {
              return;
            }
            return selectActions(
              mapSelectedSeriesToTimeseries(selectedSeries, allSeriesData),
              mapSelectionDomainToTimeseries(selectionDomain),
            );
          }}
        />
      ) : null}
      <XYChart.XAxis
        id={xAxisId}
        type="time"
        position="bottom"
        min={xAxisConfig.min}
        max={xAxisConfig.max}
        label={xAxisConfig.label}
        disableZoom={disableZoom}
        disablePan={disablePan}
        initialZoom={initialZoom as [Date, Date] | [number, number] | undefined}
        currentZoom={currentZoom as [Date, Date] | [number, number] | undefined}
        onZoomChange={onZoomChange}
        infiniteZoom={infiniteZoom}
        gapPolicy={effectiveGapPolicy}
      />
      {renderedUnits.map((unit, index) => (
        <XYChart.YAxis
          key={defineAxisId(unit)}
          id={defineAxisId(unit)}
          unit={unit}
          {...mapYAxisSlotToProps(index, configuredAxes[index])}
          valueRepresentation={resolvedValueRepresentation}
        />
      ))}
      <XYChart.Legend {...legend} />
      {allColorRules.map((rule) => (
        <XYChart.ColorRule
          key={`${rule.comparator}-${rule.matchValue}-${rule.valueAccessor}`}
          {...rule}
        />
      ))}
      <XYChart.Toolbar
        hidden={toolbar.hidden}
        placement={toolbar.placement}
        {...extractBehaviorTrackingProps(toolbar)}
      />
      {/* TODO: Remove after deprecating ChartToolbar (APPDEV-15613) */}
      {deprecatedToolbar.enabled && (
        <XYChart.Toolbar placement={deprecatedToolbar.placement} />
      )}
      {intents.map((intentProps, index) => (
        <XYChart.Intent key={index} {...intentProps} />
      ))}
      {annotations &&
        renderXYAnnotations(
          transformTimeseriesAnnotationsToXY(annotations.tracks),
          xAxisId,
          annotations,
        )}
    </XYChart>
  );
});
